import { useCallback, useEffect, useMemo, useState } from "react";

import styled from "styled-components";

import Modal from "pages/members/components/Modal";
import { fetchMembers } from "shared/api/MemberApi";
import { decrypt } from "shared/lib/crypto/decrypt";
import { formatToKST, toKstDate } from "shared/lib/date/formatKst";
import { alertDialog } from "shared/ui/feedback";
import { SearchPanel } from "shared/ui/filters";
import { BaseContainer, PageTitle } from "shared/ui/layout";
import {
  DataTable,
  Pagination,
  useClientPagination,
} from "shared/ui/table";

const ManageBtn = styled.button`
  width: fit-content;
  height: fit-content;
  padding: 7px 23px;
  border-radius: 5px;
  background: var(--admin-blue-default-blue, #0079e0);
  color: white;
  border: #0079e0;
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
`;

const MEMBER_CATEGORY_OPTIONS = [
  { label: "일반", value: 1 },
  { label: "관리자", value: 2 },
  { label: "마스터", value: 4 },
];
const MEMBER_CONDITION_OPTIONS = ["이름", "이메일"];
const MEMBER_DATE_FIELD_OPTIONS = ["가입일", "최근 로그인"];
const MEMBER_QUICK_RANGE_OPTIONS = [
  { label: "오늘", value: 0 },
  { label: "6개월", value: 1 },
  { label: "1년", value: 2 },
];
const MEMBER_RANGE_DAYS = [1, 180, 365];

function MemberManager() {
  const normalizeMember = useCallback((member) => ({
    ...member,
    createdAtDisplay: formatToKST(member?.createdAt),
    lastLoginDisplay: formatToKST(member?.lastLogin),
  }), []);

  const [memberData, setMemberData] = useState([]);
  const [queriedMemberData, setQueriedMemberData] = useState([]);
  const [isSearched, setIsSearched] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const itemsPerPage = 10;
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [category, setCategory] = useState(0);

  const sourceData = isSearched ? queriedMemberData : memberData;
  const { pageItems, page, setPage, pageSize, totalCount } =
    useClientPagination(sourceData, { pageSize: itemsPerPage });

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      { Header: "이름", accessor: "name" },
      {
        Header: "이메일",
        accessor: "email",
        Cell: ({ row }) => {
          const decryptedEmail = decrypt(row.values.email); // 이메일 복호화
          return decryptedEmail;
        },
      },
      { Header: "가입일", accessor: "createdAtDisplay" },
      { Header: "최근 로그인", accessor: "lastLoginDisplay" },
      {
        Header: "구분",
        accessor: "category",
        Cell: ({ row }) => {
          if (row.original.auth === 4) {
            return "마스터";
          } else if (row.original.auth === 3) {
            return "총학";
          } else if (row.original.auth === 2) {
            return `관리자`;
          } else if (row.original.auth === 1) {
            return "일반";
          } else {
            return "비회원";
          }
        },
      },
      {
        Header: "상세",
        accessor: "manage",
        Cell: ({ row }) => (
          <ManageBtn
            style={{ margin: "0 auto" }}
            onClick={() => handleManageClick(row)}
          >
            상세
          </ManageBtn>
        ),
      },
    ],
    [],
  );

  // SearchPanel이 넘긴 정규화 필터로 회원을 검색한다.
  // 빠른 기간 칩(3종)은 applyDateFilter 인덱스와 의미가 달라 자체 날짜창으로 처리.
  const handleSearchFilters = (filters) => {
    const { keyword, condition, chips, quickRange, rangeField, startDate, endDate } =
      filters;
    const selectedCategory = chips?.category ?? null; // null = 전체(권한)

    // 기간 윈도우: 빠른 칩 우선, 없으면 직접 입력 범위.
    let rangeStart = null;
    let rangeEnd = null;
    if (quickRange !== null) {
      rangeEnd = new Date();
      rangeStart = new Date(rangeEnd);
      rangeStart.setDate(rangeStart.getDate() - (MEMBER_RANGE_DAYS[quickRange] ?? 0));
    } else if (startDate && endDate) {
      rangeStart = new Date(startDate);
      rangeEnd = new Date(endDate);
    }

    const useLastLogin = rangeField === MEMBER_DATE_FIELD_OPTIONS[1];

    const queried = memberData.filter((member) => {
      // 가입일/최근 로그인은 서버 UTC라 KST 보정 후 비교.
      const memberDate = toKstDate(
        useLastLogin ? member.lastLogin : member.createdAt,
      );
      const isWithinDateRange =
        (!rangeStart || (memberDate && memberDate >= rangeStart)) &&
        (!rangeEnd || (memberDate && memberDate <= rangeEnd));

      const includesSearchTerm =
        (condition === MEMBER_CONDITION_OPTIONS[0] &&
          String(member.name ?? "").includes(keyword)) ||
        (condition === MEMBER_CONDITION_OPTIONS[1] &&
          String(member.email ?? "").includes(keyword));

      const matchesMemberCategory =
        selectedCategory === null || member.auth === selectedCategory;

      return includesSearchTerm && isWithinDateRange && matchesMemberCategory;
    });

    if (queried.length === 0) {
      alertDialog(
        "해당 검색어를 찾을 수 없습니다.\n검색어를 다시 확인하시고 검색해주세요.",
      );
    } else {
      setQueriedMemberData(queried);
      setIsSearched(true);
    }
  };

  const handleReset = () => {
    setIsSearched(false);
    setQueriedMemberData([]);
    setPage(1);
  };

  const readMembers = useCallback(async () => {
    try {
      const members = await fetchMembers();
      const normalizedData = (members || [])
        .map(normalizeMember)
        .slice()
        .reverse();
      setMemberData(normalizedData);
    } catch (error) {
    }
  }, [normalizeMember]);

  useEffect(() => {
    readMembers();
  }, [readMembers]);

  function handleManageClick(row) {
    // '설정' 버튼 클릭 시 동작 구현
    setCategory(row.original.auth);
    setName(row.original.name);
    setEmail(row.original.email);
    setCreatedAt(row.original.createdAtDisplay);
    setUpdatedAt(row.original.lastLoginDisplay);
    setIsModalOpen(true);
  }

  return (
    <BaseContainer>
      <PageTitle pageName="회원 조회" />
      <SearchPanel onSearch={handleSearchFilters} onReset={handleReset}>
        <SearchPanel.Chips
          name="category"
          label="구분"
          options={MEMBER_CATEGORY_OPTIONS}
          deselectTo={null}
        />
        <SearchPanel.Keyword
          label="조건 검색"
          dropdown={MEMBER_CONDITION_OPTIONS}
        />
        <SearchPanel.QuickRange
          label="검색 기간"
          dropdown={MEMBER_DATE_FIELD_OPTIONS}
          options={MEMBER_QUICK_RANGE_OPTIONS}
        />
        <SearchPanel.DateRange />
      </SearchPanel>
      <div style={{ marginBottom: "18px" }}>
        총{" "}
        <span style={{ color: "var(--admin-blue-default-blue, #0079E0)" }}>
          {totalCount}
        </span>
        명
      </div>
      <DataTable columns={columns} data={pageItems} />
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
      <Modal
        // 모달 창 오픈
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        adminPage={false}
        // 회원 정보 관련
        name={name}
        email={decrypt(email)}
        createdAt={createdAt}
        updatedAt={updatedAt}
        category={category}
      />
    </BaseContainer>
  );
}

export default MemberManager;
